package com.ashes.dev.ai.weather.weathershow

import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.drawscope.rotate

@Composable
fun CloudIcon(modifier: Modifier = Modifier, color: Color = Color.LightGray) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val path = Path().apply {
            moveTo(w * 0.2f, h * 0.7f)
            quadraticTo(w * 0.05f, h * 0.7f, w * 0.1f, h * 0.55f)
            quadraticTo(w * 0.1f, h * 0.35f, w * 0.3f, h * 0.35f)
            quadraticTo(w * 0.35f, h * 0.15f, w * 0.6f, h * 0.2f)
            quadraticTo(w * 0.85f, h * 0.2f, w * 0.85f, h * 0.45f)
            quadraticTo(w * 0.95f, h * 0.5f, w * 0.9f, h * 0.7f)
            close()
        }
        drawPath(path = path, color = color)
    }
}

@Composable
fun RainIcon(modifier: Modifier = Modifier, color: Color = Color(0xFF64B5F6)) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val cloudPath = Path().apply {
            moveTo(w * 0.2f, h * 0.5f)
            quadraticTo(w * 0.05f, h * 0.5f, w * 0.1f, h * 0.35f)
            quadraticTo(w * 0.1f, h * 0.2f, w * 0.3f, h * 0.2f)
            quadraticTo(w * 0.35f, h * 0.05f, w * 0.6f, h * 0.1f)
            quadraticTo(w * 0.85f, h * 0.1f, w * 0.85f, h * 0.35f)
            quadraticTo(w * 0.95f, h * 0.4f, w * 0.9f, h * 0.5f)
            close()
        }
        drawPath(path = cloudPath, color = Color.Gray)
        
        val dropWidth = w * 0.05f
        for (i in 0..2) {
            val x = w * (0.3f + i * 0.2f)
            drawLine(
                color = color,
                start = androidx.compose.ui.geometry.Offset(x, h * 0.6f),
                end = androidx.compose.ui.geometry.Offset(x - w * 0.05f, h * 0.8f),
                strokeWidth = dropWidth
            )
        }
    }
}

@Composable
fun SnowIcon(modifier: Modifier = Modifier, color: Color = Color.White) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val centerX = w / 2
        val centerY = h / 2
        val radius = size.minDimension * 0.4f
        
        for (i in 0 until 6) {
            val angle = i * 60f
            rotate(angle) {
                drawLine(
                    color = color,
                    start = androidx.compose.ui.geometry.Offset(centerX, centerY),
                    end = androidx.compose.ui.geometry.Offset(centerX, centerY - radius),
                    strokeWidth = w * 0.05f
                )
            }
        }
    }
}

@Composable
fun WindIcon(modifier: Modifier = Modifier, color: Color = Color.White) {
    Canvas(modifier = modifier) {
        val w = size.width
        val h = size.height
        val stroke = w * 0.08f
        
        drawLine(
            color = color,
            start = androidx.compose.ui.geometry.Offset(w * 0.1f, h * 0.3f),
            end = androidx.compose.ui.geometry.Offset(w * 0.7f, h * 0.3f),
            strokeWidth = stroke
        )
        drawArc(
            color = color,
            startAngle = -90f,
            sweepAngle = 180f,
            useCenter = false,
            topLeft = androidx.compose.ui.geometry.Offset(w * 0.6f, h * 0.15f),
            size = androidx.compose.ui.geometry.Size(w * 0.3f, h * 0.3f),
            style = Stroke(width = stroke)
        )
        
        drawLine(
            color = color,
            start = androidx.compose.ui.geometry.Offset(w * 0.2f, h * 0.6f),
            end = androidx.compose.ui.geometry.Offset(w * 0.8f, h * 0.6f),
            strokeWidth = stroke
        )
    }
}

@Composable
fun WeatherIcon(code: Int, modifier: Modifier = Modifier) {
    when (code) {
        0 -> SunIcon(modifier)
        1, 2, 3 -> CloudIcon(modifier)
        45, 48 -> CloudIcon(modifier, color = Color.Gray)
        51, 53, 55, 61, 63, 65 -> RainIcon(modifier)
        71, 73, 75 -> SnowIcon(modifier)
        95 -> RainIcon(modifier, color = Color.Red) // Thunderstorm
        else -> CloudIcon(modifier)
    }
}
